/**
 * Reception Types for MEV-Med System
 * TypeScript interfaces for reception management
 *
 * @version 2025.1.0
 */

import { AppointmentResponse } from './appointment';

/**
 * Enum for Attendance Status
 */
export enum AttendanceStatus {
  NOT_ATTENDED = 'NOT_ATTENDED',
  CHECKED_IN = 'CHECKED_IN',
  TRIAGED = 'TRIAGED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW'
}

/**
 * Enum for Attendance Type
 */
export enum AttendanceType {
  FULL = 'FULL',    // Full attendance (triage + medical consultation)
  QUICK = 'QUICK'   // Quick attendance (triage only, handled by reception)
}

/**
 * Labels for attendance status display
 */
export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  [AttendanceStatus.NOT_ATTENDED]: 'Aguardando',
  [AttendanceStatus.CHECKED_IN]: 'Check-in',
  [AttendanceStatus.TRIAGED]: 'Triagem Feita',
  [AttendanceStatus.IN_PROGRESS]: 'Em Atendimento',
  [AttendanceStatus.COMPLETED]: 'Atendido',
  [AttendanceStatus.NO_SHOW]: 'Faltou'
};

/**
 * Colors for attendance status badges
 */
export const ATTENDANCE_STATUS_COLORS: Record<AttendanceStatus, 'default' | 'info' | 'warning' | 'success' | 'error' | 'secondary'> = {
  [AttendanceStatus.NOT_ATTENDED]: 'default',
  [AttendanceStatus.CHECKED_IN]: 'info',
  [AttendanceStatus.TRIAGED]: 'secondary',
  [AttendanceStatus.IN_PROGRESS]: 'warning',
  [AttendanceStatus.COMPLETED]: 'success',
  [AttendanceStatus.NO_SHOW]: 'error'
};

/**
 * Reception appointment response (extends AppointmentResponse with reception fields)
 */
export interface ReceptionAppointment extends AppointmentResponse {
  attendanceStatus: AttendanceStatus;
  attendanceType: AttendanceType;
  presenceConfirmedAt: string | null;
  attendanceStartedAt: string | null;
  attendanceCompletedAt: string | null;
  queueNumber: number | null;
  queueCalledAt: string | null;
  receptionistId: number | null;
  receptionistName: string | null;
}

/**
 * Reception filters
 */
export interface ReceptionFilters {
  date?: string;
  clinicId?: number;
  companyId?: number;
  attendanceStatus?: AttendanceStatus | AttendanceStatus[];
  search?: string;
  period?: 'morning' | 'afternoon' | 'all';
}

/**
 * Reception list parameters
 */
export interface ReceptionListParams extends ReceptionFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Reception list response
 */
export interface ReceptionListResponse {
  appointments: ReceptionAppointment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Reception summary
 */
export interface ReceptionSummary {
  total: number;
  morning: number;
  afternoon: number;
  byStatus: Record<string, number>;
}

/**
 * Period options for filtering
 */
export const PERIOD_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'morning', label: 'Manhã' },
  { value: 'afternoon', label: 'Tarde' }
] as const;

/**
 * Attendance status options for filtering
 */
export const ATTENDANCE_STATUS_OPTIONS = Object.entries(ATTENDANCE_STATUS_LABELS).map(
  ([value, label]) => ({ value: value as AttendanceStatus, label })
);

/**
 * Get available next actions for an attendance status
 */
export function getAvailableActions(status: AttendanceStatus): string[] {
  switch (status) {
    case AttendanceStatus.NOT_ATTENDED:
      return ['check-in', 'no-show'];
    case AttendanceStatus.CHECKED_IN:
      return ['triage', 'start-attendance', 'no-show'];
    case AttendanceStatus.TRIAGED:
      return ['start-attendance', 'no-show'];
    case AttendanceStatus.IN_PROGRESS:
      return ['complete-attendance'];
    case AttendanceStatus.COMPLETED:
    case AttendanceStatus.NO_SHOW:
      return [];
    default:
      return [];
  }
}

/**
 * Check if a status transition is valid
 */
export function isValidStatusTransition(from: AttendanceStatus, to: AttendanceStatus): boolean {
  const validTransitions: Record<AttendanceStatus, AttendanceStatus[]> = {
    [AttendanceStatus.NOT_ATTENDED]: [AttendanceStatus.CHECKED_IN, AttendanceStatus.NO_SHOW],
    [AttendanceStatus.CHECKED_IN]: [AttendanceStatus.TRIAGED, AttendanceStatus.IN_PROGRESS, AttendanceStatus.NO_SHOW],
    [AttendanceStatus.TRIAGED]: [AttendanceStatus.IN_PROGRESS, AttendanceStatus.NO_SHOW],
    [AttendanceStatus.IN_PROGRESS]: [AttendanceStatus.COMPLETED],
    [AttendanceStatus.COMPLETED]: [],
    [AttendanceStatus.NO_SHOW]: []
  };

  return validTransitions[from]?.includes(to) ?? false;
}

/**
 * Format queue number for display
 */
export function formatQueueNumber(queueNumber: number | null): string {
  if (queueNumber === null) return '-';
  return String(queueNumber).padStart(3, '0');
}

/**
 * Format time for display
 */
export function formatTime(hour: string | null, minute: string | null, period?: string | null): string {
  if (!hour && period === 'morning') return 'Manhã';
  if (!hour && period === 'afternoon') return 'Tarde';
  if (!hour) return '-';
  const h = hour.padStart(2, '0');
  const m = (minute || '0').padStart(2, '0');
  return `${h}:${m}`;
}
